import { apiSearchResults, apiGetFilterOptions } from '../api.ts'
import type { ISearchStore, SearchFilterOption } from '../types.ts'
import { COLLECTIONS_SEARCH_TAB, COLLECTIONS_SEARCH_SORT_OPTIONS, COLLECTIONS_SEARCH_SORT_DEFAULT } from '../constants.ts'
import type {ApiEndPointMappingKey} from '@/components/Search/types.ts'
import { getStatusDefaultOption, mapStatusOption, sortStatusOptions } from './statusFacetHelpers.ts'
import { gaTrackUserSearch } from '@/utils/gaHelpers.ts'

const normalizeStatusOptions = (options: any[], includeCount = false): SearchFilterOption[] => {
  const statusOptions: SearchFilterOption[] = []
  for (const option of options || []) {
    statusOptions.push(mapStatusOption(option, includeCount))
  }
  return sortStatusOptions(statusOptions)
}

export default {
  initCollectionSearchBarFilterOptions: async function (this: ISearchStore) {
    // Map of fields to local lookup keys
    const filterOptionMapping: Record<string, string> = {
      [`${COLLECTIONS_SEARCH_TAB}.collections`]: `lookUpOptions.${COLLECTIONS_SEARCH_TAB}.collections`,
      [`${COLLECTIONS_SEARCH_TAB}.companyCollection`]: `lookUpOptions.${COLLECTIONS_SEARCH_TAB}.companyCollection`,
      [`${COLLECTIONS_SEARCH_TAB}.tags`]: `lookUpOptions.${COLLECTIONS_SEARCH_TAB}.tags`,
    };

    // Create an array of promises for all API requests
    const fetchPromises = Object.entries(filterOptionMapping).map(async ([field, lookupKey]) => {
      try {
        const loadedOptions = await apiGetFilterOptions(field as ApiEndPointMappingKey);

        const options = Array.isArray(loadedOptions)
          ? loadedOptions.map((option: any) => ({
              id: option.id ?? '',
              text: option.text ?? '',
            }))
          : [];

        return { lookupKey, options };
      } catch (error) {
        console.error(`Failed to load filter options for ${field}:`, error);
        return { lookupKey, options: [] };
      }
    });

    // Wait for all API calls to finish
    const results = await Promise.all(fetchPromises);

    // Assign results to store
    for (const { lookupKey, options } of results) {
      (this as any)[lookupKey] = options;
    }
  },
  resetCollectionsFiltersForMainSearch: function(this: ISearchStore, facetFilter: boolean = false) {
    // reset all search filters except document number, if search results are displayed
    //@ts-ignore
    if (this.isResultLoaded(COLLECTIONS_SEARCH_TAB)) {
      this.clearSearchBarFilters(COLLECTIONS_SEARCH_TAB, ['documentNumber'])
      if (!facetFilter) {
        const selectedStatusOption = getStatusDefaultOption((this as any)?.filterDefaults?.status ?? '');
        this[`${COLLECTIONS_SEARCH_TAB}.status`] = selectedStatusOption ? [selectedStatusOption] : [];
      }
    }
    if(!facetFilter) {
      //if search from search bar reset all facet filters, apply only the search bar filters
      this.clearSearchFacetFilters(COLLECTIONS_SEARCH_TAB, ['documentNumber']);
    }
    
  },
  getCollectionSearchParams: function(this: ISearchStore, facetFilter: boolean = false, pageNo: number = 1 ) {
    this.resetCollectionsFiltersForMainSearch(facetFilter);
    let params: any = {
        'name': this[`${COLLECTIONS_SEARCH_TAB}.documentNumber`].trim(),
        'notes': '',
        'filter-tags': [],
        'filter-include': [],
        'filter-type': '',
        'filter-colls': [],
        'filter-dcoll': '',
        'filter-exclude': [],
        'filter-icsCode': '',
        'filter-icsCodeLevel1':'',
        'filter-icsCodeLevel2':'',
        'filter-icsCodeLevel3':'',
        'orderBy': '0',
        'page':  String(pageNo ?? 1),
        'mainsearch': 1 //set to 1 if the search is performd from the search bar
    }

    //use search bar filter values
    let filterPrefix = `${COLLECTIONS_SEARCH_TAB}`;
    //if facetFilter is true, use the facetFilter values
    if(facetFilter) {
      filterPrefix = `${COLLECTIONS_SEARCH_TAB}.facetFilter`;
    }
    //set tags 
    let tagList = (this as any)[filterPrefix + '.tags'];
    let tags = [];
    if(tagList.length > 0) {
      for (let tg of tagList) {
          tags.push(tg.id);
      }
    }
    params['filter-tags'] = tags.join(',');

    //set status
    let status = (this as any)[filterPrefix + '.status'];
    let selectedStatus = [];
    if(status.length > 0) {
      for (let statusItem of status) {
          selectedStatus.push(statusItem.id);
      }
    }
    params['filter-include'] = selectedStatus.join(',');

    //set document type
    let documentType = (this as any)[filterPrefix + '.documentType'];
    let selectedDocTypes = [];
    if(documentType.length > 0) {
      for (let dt of documentType) {
          selectedDocTypes.push(dt.id);
      }
    }
    params['filter-type'] = selectedDocTypes.join(',');

    //set collections
    let colls = (this as any)[filterPrefix + '.collections'];
    let selectedColls = [];
    if(colls.length > 0) {
      for (let coll of colls) {
        selectedColls.push(coll.id);
      }
    }
    params['filter-colls'] = selectedColls.join(',');

    //set company collections
    let dcolls = (this as any)[filterPrefix + '.companyCollection'];
    let selectedDColls = [];
    if(dcolls.length > 0) {
      for(let dcoll of dcolls) {
        selectedDColls.push(dcoll.id);
      }
    }
    params['filter-dcoll'] = selectedDColls.join(',');
    
    if(this[`${COLLECTIONS_SEARCH_TAB}.result.sort`].length > 0) {
      params['orderBy'] = this[`${COLLECTIONS_SEARCH_TAB}.result.sort`][0].id;
    }

    params['notes'] = String(this[`${filterPrefix}.notes`] ?? '').trim();
    params['filter-icsCode'] = String(this[`${filterPrefix}.icsCode`] ?? '').trim();
    params['filter-icsCodeLevel1'] = String(this[`${filterPrefix}.descriptorField`]?.map((item: any) => item.id).join(',') ?? '').trim();
    params['filter-icsCodeLevel2'] = String(this[`${filterPrefix}.descriptorGroup`]?.map((item: any) => item.id).join(',') ?? '').trim();
    params['filter-icsCodeLevel3'] = String(this[`${filterPrefix}.descriptorSubGroup`]?.map((item: any) => item.id).join(',') ?? '').trim();
    
    return params
  },
  setCollectionSearchResultData: function(this: ISearchStore, resultData: any) {
    this[`${COLLECTIONS_SEARCH_TAB}.resultData`] = resultData;
    this[`${COLLECTIONS_SEARCH_TAB}.documentNumber`] = resultData.initialQuery?.name ?? '';
    //for each filters, set the lookup fields
    const facetFilterMapping: Record<string, string> = {
      'publisher': 'publisher',
      'include': 'status',
      'types': 'documentType',
      'colls': 'collections',
      'dcolls': 'companyCollection',
      'tags': 'tags',
      'dataset': 'dataset',
      "ics_level1": 'descriptorField',
      "ics_level2": 'descriptorGroup',
      "ics_level3": 'descriptorSubGroup',
    };

    for (const [apiKey, stateKey] of Object.entries(facetFilterMapping)) {
      //selected options from api response
      let apiReturnedSelectedOptions = resultData?.filters?.[apiKey]?.filter((option: any) => option.selected).map((option: any) => ({
          id: option?.id?.toString() ?? '',
          text: option?.value?.toString() ?? ''
      })) || [];
      if (apiKey === 'include') {
        apiReturnedSelectedOptions = normalizeStatusOptions(apiReturnedSelectedOptions);
      }

      //selected search bar options
      const selectedSearchBarOptions = (this as any)[`${COLLECTIONS_SEARCH_TAB}.${stateKey}`] || []; 

      //selected facet filter options
      let selectedFacetFilterOptions = (this as any)[`${COLLECTIONS_SEARCH_TAB}.facetFilter.${stateKey}`] || [];  
      selectedFacetFilterOptions = selectedFacetFilterOptions.map((opt: any) => { //strip out count "(num)" from the facets selected options
        return { id: opt.id.toString(), text: opt.text.toString().replace(/\s*\(\s*\d+\s*\)\s*$/, '') };
      });

      //combine selected search bar and facet filter options
      //also remove duplicates from search bar selections
      const combinedSelectedOptions = [...selectedFacetFilterOptions, ...selectedSearchBarOptions.filter((prevOpt: any) => {
        return !selectedFacetFilterOptions.some((selOpt: any) => selOpt.id.toString() === prevOpt.id.toString());
      })];

      //remove duplicates from the (selected search bar + facet filter) selections
      //and combine selected options from api and (selected search bar + facet filter)
      //so we have selected options from api response + user selected options(search bar + facet filter) as api wlll not have all user selected options
      (this as any)[`${COLLECTIONS_SEARCH_TAB}.facetFilter.${stateKey}`] = [...apiReturnedSelectedOptions, ...combinedSelectedOptions.filter((prevOpt: any) => {
        return !apiReturnedSelectedOptions.some((selOpt: any) => selOpt.id.toString() === prevOpt.id.toString());
      })];

      const finalSelectedFilterOptions = (this as any)[`${COLLECTIONS_SEARCH_TAB}.facetFilter.${stateKey}`] || [];
      if(apiKey == 'ics_level1') {
        this.loadICSCodeOptions({ field: `lookUpOptions.${COLLECTIONS_SEARCH_TAB}.facetFilter.descriptorGroup`, icsCode: finalSelectedFilterOptions, level: 2 })        
      } else if(apiKey == 'ics_level2') {
         this.loadICSCodeOptions({ field: `lookUpOptions.${COLLECTIONS_SEARCH_TAB}.facetFilter.descriptorSubGroup`, icsCode: finalSelectedFilterOptions, level: 3 })        
      } else if(apiKey !='ics_level3') { //for rest of the filters except ics_level1, ics_level2, ics_level3
        let options = resultData?.filters?.[apiKey]?.map((option: any) => ({
            id: option?.id?.toString() ?? '',
            text: option?.value?.toString() ?? ''
        })) ?? [];
        if (apiKey === 'include') {
          options = normalizeStatusOptions(resultData?.filters?.[apiKey] || [], true);
        }
        (this as any)[`lookUpOptions.${COLLECTIONS_SEARCH_TAB}.facetFilter.${stateKey}`] = options;
      }
    }
    (this as any)[`${COLLECTIONS_SEARCH_TAB}.facetFilter.icsCode`] = resultData.initialQuery?.['icsCode'] ? resultData.initialQuery?.['icsCode'] : '';
    (this as any)[`${COLLECTIONS_SEARCH_TAB}.facetFilter.notes`] = resultData.initialQuery?.['notes'] ? resultData.initialQuery?.['notes'] : '';
    (this as any)[`${COLLECTIONS_SEARCH_TAB}.activeCollectionId`] =  resultData.initialQuery?.['active_collection_id'] ? resultData.initialQuery?.['active_collection_id'] : '';
    (this as any)[`${COLLECTIONS_SEARCH_TAB}.activeCollectionName`] =  resultData.initialQuery?.['active_collection_name'] ? resultData.initialQuery?.['active_collection_name'] : '';

   
    this[`${COLLECTIONS_SEARCH_TAB}.result.sort`] = [
      COLLECTIONS_SEARCH_SORT_OPTIONS.find(
        opt => opt.id === String(resultData.orderBy)
      ) ?? COLLECTIONS_SEARCH_SORT_DEFAULT
    ];
    this[`${COLLECTIONS_SEARCH_TAB}.result.page`] = resultData.page ?? 1;
  },
  async searchCollections(this: ISearchStore, facetFilter: boolean = false, pageNo: number = 1) {
    try {
      this['searchResultActive'] = true; 
      this[`${COLLECTIONS_SEARCH_TAB}.result.loading`] = true;
      let params = this.getCollectionSearchParams(facetFilter, pageNo);
      const collSearchApiAbortController = this.addApiAbortController('collectionSearchApi');
      let results = await apiSearchResults(COLLECTIONS_SEARCH_TAB, params, collSearchApiAbortController);
      if(results.status != 'error') {
        this.setCollectionSearchResultData(results.data);
        gaTrackUserSearch(COLLECTIONS_SEARCH_TAB, params, this[`${COLLECTIONS_SEARCH_TAB}.resultData`]);
      }
      this[`${COLLECTIONS_SEARCH_TAB}.result.loading`] = false;
    } catch (e: any) {
      if((e?.code??'') == 'ERR_CANCELED') return; //if the error is due to request cancellation, do nothing
      this[`${COLLECTIONS_SEARCH_TAB}.result.loading`] = false;
      console.log('collections search failed');
    }
  },
  exportCollectionSearchResults: async function(this: ISearchStore) {
    try {
      this[`${COLLECTIONS_SEARCH_TAB}.export.status`] = 'inprogress';
      let params = this.getCollectionSearchParams(true, 1); //use facet filters for export
      //add export specific params
      const exportFormat = this[`${COLLECTIONS_SEARCH_TAB}.export.format`];
      const selectedFields = this[`${COLLECTIONS_SEARCH_TAB}.export.selectedFields`];
      params['csvSeparator'] = exportFormat;
      params['fieldsToExport'] = selectedFields.join(',');
      params['nonce'] = this['nonce'];
      let results = await apiSearchResults(`${COLLECTIONS_SEARCH_TAB}/export`, params);
      if(results.status != 'error') {
          this[`${COLLECTIONS_SEARCH_TAB}.export.totalFound`] = results?.data?.total_found;
          this[`${COLLECTIONS_SEARCH_TAB}.export.status`] = 'complete';
      } else if(results.status == 'error' && results.code == "401") { //if nolonger authenticated go to home page
          this[`${COLLECTIONS_SEARCH_TAB}.export.status`] = 'completewitherror';
          document.location = '/management/home/index';
      } else {
          this[`${COLLECTIONS_SEARCH_TAB}.export.status`] = 'completewitherror';
      }
    } catch(error) {
        this[`${COLLECTIONS_SEARCH_TAB}.export.status`] = 'completewitherror';
        console.log('export collection search results failed.');
        console.log(error);
    }
  }
}
